import * as React from 'react';
import { useNavigate} from 'react-router-dom';
import { Command} from 'cmdk';
import { viewSlugForUrl } from '../utils/urlHelpers';
import {
 Search,
 ArrowRight,
 Sun,
 Moon,
 Sparkles,
 LayoutGrid,
 Compass,
 Activity,
 Clock4,
 Gauge,
 Maximize2,
 BookOpen,
 ExternalLink,
} from 'lucide-react';

import { viewRegistry, actionRegistry, moduleManifestRegistry} from '../registries';
import { filterByModuleAccess, type ModuleAccess } from './entitlementGate';
import {
 toggleTheme as togglePreferredTheme,
 setTheme,
 setDensity as setPreferredDensity,
 type DensityPreference,
} from './uiPreferences';

interface CommandPaletteProps {
 open: boolean;
 onOpenChange: (open: boolean) => void;
 /**
  * The shell's entitlement gate. Every navigable item below is filtered
  * through it: an unentitled module must not be listed, must not be
  * keyboard-focusable via ⌘K search, and must not be reachable by selecting a
  * view, action, or help topic that belongs to it. When the gate is not
  * `ready` the palette offers ONLY the shell's own toggles.
  */
 moduleAccess: ModuleAccess;
}

/**
 * Tyndo Command Palette — Cmd+K entry to nav, actions, recents, theme/density/mode.
 *
 * Sources:
 * - viewRegistry → all module views
 * - menuRegistry → top-level menu items
 * - actionRegistry → registered cross-module actions
 * - shell-built-in toggles: theme, density, copilot, plant canvas
 */
export default function CommandPalette({
 open,
 onOpenChange,
 moduleAccess,
}: CommandPaletteProps) {
 const navigate = useNavigate();
 const [query, setQuery] = React.useState('');
 const inputRef = React.useRef<HTMLInputElement>(null);

 // Focus the search field whenever the palette opens (replaces autoFocus so
 // the field still receives focus without the jsx-a11y/no-autofocus prop).
 React.useEffect(() => {
 if (open) inputRef.current?.focus();
}, [open]);

 // Snapshot of the ViewRegistry, refreshed on every registry mutation so the
 // lazily-collected item lists recompute when a module registers its views via
 // Pass 2 (federated bundle load) while the palette is closed. Without this the
 // memos below — which would otherwise depend on `[open]` alone — would surface
 // a stale snapshot captured at the last open, and the operator would have to
 // close+reopen to see views that registered in the meantime.
 const [registryViews, setRegistryViews] = React.useState(() =>
 viewRegistry.listViews()
 );
 React.useEffect(() => {
 // Seed once on mount in case a registration landed between render and effect,
 // then keep in lockstep with every subsequent mutation.
 setRegistryViews(viewRegistry.listViews());
 return viewRegistry.subscribe(() => {
 setRegistryViews(viewRegistry.listViews());
 });
 }, []);

 // Close on Escape (cmdk handles arrow keys + Enter)
 React.useEffect(() => {
 if (!open) {
 setQuery('');
}
}, [open]);

 // Entitlement-bounded view snapshot. EVERY list below derives from this, so
 // a view belonging to an unentitled module can never reach the palette — not
 // as a nav item, not as a module row, not as a help topic. Views with no
 // moduleId are dropped: an untagged navigable target cannot be authorized.
 const permittedViews = React.useMemo(
 () => filterByModuleAccess(moduleAccess, registryViews, (v) => v.moduleId),
 [moduleAccess, registryViews]
 );

 // Lazy-collect items every time the palette opens — registries can grow
 const navItems = React.useMemo(() => {
 if (!open) return [];
 const allViews = permittedViews;
 const seen = new Set<string>();
 const items: { id: string; label: string; subtitle: string; href: string; moduleId: string}[] = [];
 allViews.forEach((v) => {
 const moduleId = v.moduleId ?? '';
 const key = `${moduleId}:${v.viewId}`;
 if (seen.has(key)) return;
 seen.add(key);
 items.push({
 id: key,
 label: v.viewName || v.viewId || moduleId,
 subtitle: v.moduleName || moduleId,
 href: `/${moduleId}/${viewSlugForUrl(v.viewId)}`,
 moduleId,
});
});
 return items;
}, [open, permittedViews]);

 const moduleItems = React.useMemo(() => {
 if (!open) return [];
 const allViews = permittedViews;
 const map = new Map<string, { moduleId: string; name: string; defaultView: string}>();
 allViews.forEach((v) => {
 const moduleId = v.moduleId ?? '';
 if (!map.has(moduleId)) {
 map.set(moduleId, {
 moduleId,
 name: v.moduleName ?? moduleId,
 defaultView: v.viewId,
});
}
});
 return Array.from(map.values());
}, [open, permittedViews]);

 const actionItems = React.useMemo(() => {
 if (!open) return [];
 // Cross-module actions navigate into their owning module — gate them on the
 // same set. An action with no moduleId is dropped rather than trusted.
 const all = filterByModuleAccess(
 moduleAccess,
 actionRegistry.listActions?.() ?? [],
 (a) => a.moduleId,
 );
 return all.map((a) => ({
 id: a.actionId,
 label: a.label ?? a.actionId,
 subtitle: a.moduleId ?? '',
 viewId: a.viewId,
 moduleId: a.moduleId,
}));
}, [open, moduleAccess]);

 // Help / documentation topics. Operators need a quick way to understand what
 // each view is for without clicking in. We assemble a searchable index from:
 //   1. Per-module `documentation` arrays declared on the manifest
 //      ({ title, url, keywords }) — these open the external doc in a new tab.
 //   2. Per-view descriptive metadata (description / subtitle / documentation)
 //      so "what is the <view> page" is answerable — selecting one navigates to
 //      the view itself (contextual landing).
 // Fields are read defensively because the shared manifest/view types don't
 // (yet) declare `documentation`/`description` on every shape; modules that
 // ship them get richer help, modules that don't still get the view's own
 // name + module as a searchable topic.
 const helpItems = React.useMemo(() => {
 if (!open) return [];
 type HelpItem = {
 id: string;
 title: string;
 subtitle: string;
 keywords: string;
 href?: string;
 url?: string;
};
 const items: HelpItem[] = [];
 const seen = new Set<string>();

 // (1) Module-level documentation links from the manifest — gated, because a
 // help topic leaks the module's NAME, DESCRIPTION and doc URL even when it
 // never navigates anywhere.
 filterByModuleAccess(
 moduleAccess,
 moduleManifestRegistry.list(),
 (mod) => mod.moduleId,
 ).forEach((mod) => {
 const docs = (mod as { documentation?: Array<{ title?: string; url?: string; keywords?: string[] }> })
 .documentation;
 if (Array.isArray(docs)) {
 docs.forEach((doc, i) => {
 if (!doc?.title && !doc?.url) return;
 const id = `doc:${mod.moduleId}:${i}`;
 if (seen.has(id)) return;
 seen.add(id);
 items.push({
 id,
 title: doc.title ?? mod.name,
 subtitle: mod.name,
 keywords: [doc.title, mod.name, mod.moduleId, ...(doc.keywords ?? [])]
 .filter(Boolean)
 .join(' '),
 url: doc.url,
});
});
}
 // Module description as a standalone help topic.
 if (mod.description) {
 const id = `mod-desc:${mod.moduleId}`;
 if (!seen.has(id)) {
 seen.add(id);
 items.push({
 id,
 title: `About ${mod.name}`,
 subtitle: mod.description,
 keywords: `${mod.name} ${mod.moduleId} ${mod.description}`,
});
}
}
});

 // (2) Per-view descriptive metadata (already entitlement-bounded).
 permittedViews.forEach((v) => {
 const loose = v as typeof v & {
 description?: string;
 documentation?: string | Array<{ title?: string; url?: string; keywords?: string[] }>;
};
 const moduleId = v.moduleId ?? '';
 const description =
 loose.description ??
 (typeof loose.documentation === 'string' ? loose.documentation : undefined) ??
 v.subtitle;
 if (!description) return;
 const id = `view-help:${moduleId}:${v.viewId}`;
 if (seen.has(id)) return;
 seen.add(id);
 items.push({
 id,
 title: v.viewName || v.viewId,
 subtitle: description,
 keywords: `${v.viewName} ${v.viewId} ${v.moduleName ?? moduleId} ${description}`,
 href: `/${moduleId}/${viewSlugForUrl(v.viewId)}`,
});
});

 return items;
}, [open, permittedViews, moduleAccess]);

 const dispatch = (run: () => void) => {
 run();
 onOpenChange(false);
};

 // Theme + density route through the shared preference store so palette
 // toggles persist AND sync to every other open Tyndo tab.
 const toggleTheme = () => togglePreferredTheme();
 const setDensity = (density: DensityPreference) => setPreferredDensity(density);

 if (!open) return null;

 return (
 <div
 role="presentation"
 className="fixed inset-0 z-[60] flex items-start justify-center md:px-4 md:pt-[12vh]"
 onClick={(e) => {
 if (e.target === e.currentTarget) onOpenChange(false);
}}
 onKeyDown={(e) => {
 if (e.key === 'Escape') onOpenChange(false);
}}
 >
 {/* Backdrop */}
 <div
 className="absolute inset-0 bg-foreground/30 backdrop-blur-sm animate-in fade-in-0"
 aria-hidden
 />

 {/* Below md the palette is a full-screen sheet (100dvh + safe-area
 padding) so a phone/tablet operator gets the whole viewport for
 search; at md+ it stays the centered floating panel. */}
 <Command
 label="Tyndo command palette"
 className="relative z-10 flex h-[100dvh] w-full max-w-none flex-col overflow-hidden rounded-none border-0 bg-surface-overlay pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] shadow-[var(--shadow-overlay)] animate-in fade-in-0 slide-in-from-top-2 md:h-auto md:max-w-xl md:rounded-xl md:border md:border-border/60 md:pt-0 md:pb-0 md:zoom-in-95"
 loop
 shouldFilter={true}
 >
 <div className="flex items-center gap-2 border-b border-border px-4 py-3">
 <Search className="h-4 w-4 text-muted-foreground shrink-0" />
 <Command.Input
 ref={inputRef}
 value={query}
 onValueChange={setQuery}
 placeholder="Search modules, actions, or jump to…"
 className="flex-1 bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
 />
 <kbd className="hidden sm:inline-flex items-center gap-0.5 rounded border border-border bg-surface-sunken px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground t-mono">
 ESC
 </kbd>
 </div>

 <Command.List className="max-h-none flex-1 overflow-y-auto p-2 md:max-h-[60vh] md:flex-none">
 <Command.Empty className="py-6 text-center text-sm text-muted-foreground">
 No matches.
 </Command.Empty>

 {/* Explicit entitlement state. Modules/views/actions/help are omitted
 entirely until access resolves, and a failed check says so instead
 of listing the catalogue. */}
 {moduleAccess.state === 'loading' && (
 <p role="status" data-testid="palette-entitlements-loading" className="px-2 py-3 text-xs text-muted-foreground">
 Checking your access…
 </p>
 )}
 {moduleAccess.state === 'unavailable' && (
 <p role="alert" data-testid="palette-entitlements-unavailable" className="px-2 py-3 text-xs text-alarm-critical">
 Your access could not be verified, so no modules are listed.
 </p>
 )}

 {/* Modules */}
 {moduleItems.length > 0 && (
 <Command.Group heading="Modules" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-muted-foreground">
 {moduleItems.map((m) => (
 <Command.Item
 key={m.moduleId}
 value={`module ${m.name} ${m.moduleId}`}
 onSelect={() =>
 dispatch(() =>
 navigate(`/${m.moduleId}/${viewSlugForUrl(m.defaultView as string)}`)
 )
}
 className="group flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent aria-selected:text-foreground"
 >
 <LayoutGrid className="h-4 w-4 text-muted-foreground" />
 <span className="flex-1 truncate">{m.name}</span>
 <span className="text-xs text-muted-foreground t-mono">
 {m.moduleId.replace('tyndo.','')}
 </span>
 <ArrowRight className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-aria-selected:opacity-100" />
 </Command.Item>
 ))}
 </Command.Group>
 )}

 {/* Views (more granular) */}
 {navItems.length > 0 && (
 <Command.Group heading="Views" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-muted-foreground">
 {navItems.slice(0, 30).map((v) => (
 <Command.Item
 key={v.id}
 value={`view ${v.label} ${v.subtitle}`}
 onSelect={() => dispatch(() => navigate(v.href))}
 className="group flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Compass className="h-4 w-4 text-muted-foreground" />
 <span className="flex-1 truncate">{v.label}</span>
 <span className="text-xs text-muted-foreground">{v.subtitle}</span>
 </Command.Item>
 ))}
 </Command.Group>
 )}

 {/* Actions */}
 {actionItems.length > 0 && (
 <Command.Group heading="Actions" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-muted-foreground">
 {actionItems.slice(0, 12).map((a) => (
 <Command.Item
 key={a.id}
 value={`action ${a.label} ${a.subtitle}`}
 onSelect={() => {
 if (a.viewId && a.moduleId) {
 dispatch(() =>
 navigate(`/${a.moduleId}/${viewSlugForUrl(a.viewId as string)}`)
 );
} else {
 // No navigation target — selecting the item just closes the palette.
 onOpenChange(false);
}
}}
 className="group flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Sparkles className="h-4 w-4 text-muted-foreground" />
 <span className="flex-1 truncate">{a.label}</span>
 <span className="text-xs text-muted-foreground">{a.subtitle}</span>
 </Command.Item>
 ))}
 </Command.Group>
 )}

 {/* Help / Docs */}
 {helpItems.length > 0 && (
 <Command.Group heading="Help & Docs" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-muted-foreground">
 {helpItems.slice(0, 20).map((h) => (
 <Command.Item
 key={h.id}
 value={`help ${h.keywords}`}
 onSelect={() => {
 if (h.url) {
 dispatch(() => window.open(h.url,'_blank','noopener,noreferrer'));
} else if (h.href) {
 dispatch(() => navigate(h.href as string));
} else {
 // No target — selecting the item just closes the palette.
 onOpenChange(false);
}
}}
 className="group flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 {h.url ? (
 <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" />
 ) : (
 <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
 )}
 <span className="flex-1 truncate">{h.title}</span>
 <span className="max-w-[45%] truncate text-xs text-muted-foreground">
 {h.subtitle}
 </span>
 </Command.Item>
 ))}
 </Command.Group>
 )}

 {/* Toggles */}
 <Command.Group heading="Toggle" className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-widest [&_[cmdk-group-heading]]:text-muted-foreground">
 <Command.Item
 value="toggle theme dark light"
 onSelect={() => dispatch(toggleTheme)}
 className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Sun className="h-4 w-4 text-muted-foreground" />
 <span>Toggle theme</span>
 <kbd className="ml-auto rounded border border-border bg-surface-sunken px-1.5 py-0.5 text-[10px] t-mono text-muted-foreground">
 ⌘⇧L
 </kbd>
 </Command.Item>
 <Command.Item
 value="density comfortable"
 onSelect={() => dispatch(() => setDensity('comfortable'))}
 className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Gauge className="h-4 w-4 text-muted-foreground" />
 <span>Density: Comfortable</span>
 </Command.Item>
 <Command.Item
 value="density compact"
 onSelect={() => dispatch(() => setDensity('compact'))}
 className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Gauge className="h-4 w-4 text-muted-foreground" />
 <span>Density: Compact</span>
 </Command.Item>
 <Command.Item
 value="density touch"
 onSelect={() => dispatch(() => setDensity('touch'))}
 className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Maximize2 className="h-4 w-4 text-muted-foreground" />
 <span>Density: Touch (operator tablet)</span>
 </Command.Item>
 <Command.Item
 value="density wall"
 onSelect={() => dispatch(() => setDensity('wall'))}
 className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Maximize2 className="h-4 w-4 text-muted-foreground" />
 <span>Density: Wall (control room)</span>
 </Command.Item>
 <Command.Item
 value="navigate plant canvas home"
 onSelect={() => dispatch(() => navigate('/'))}
 className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Activity className="h-4 w-4 text-muted-foreground" />
 <span>Open Plant Canvas</span>
 </Command.Item>
 <Command.Item
 value="navigate browse modules grid"
 onSelect={() => dispatch(() => navigate('/browse'))}
 className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <LayoutGrid className="h-4 w-4 text-muted-foreground" />
 <span>Browse all modules</span>
 </Command.Item>
 <Command.Item
 value="dark"
 onSelect={() => dispatch(() => setTheme('dark'))}
 className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Moon className="h-4 w-4 text-muted-foreground" />
 <span>Switch to dark mode</span>
 </Command.Item>
 <Command.Item
 value="light"
 onSelect={() => dispatch(() => setTheme('light'))}
 className="flex cursor-pointer items-center gap-3 rounded-md px-2 py-1.5 text-sm text-foreground aria-selected:bg-accent"
 >
 <Sun className="h-4 w-4 text-muted-foreground" />
 <span>Switch to light mode</span>
 </Command.Item>
 </Command.Group>
 </Command.List>

 <div className="flex items-center justify-between border-t border-border px-3 py-2 text-[10px] text-muted-foreground t-mono">
 <span className="flex items-center gap-2">
 <Clock4 className="h-3 w-3" />
 tyndo · cmd palette
 </span>
 <span className="flex items-center gap-2">
 <kbd className="rounded bg-surface-sunken px-1.5 py-0.5">↵</kbd> open
 <kbd className="rounded bg-surface-sunken px-1.5 py-0.5">↑↓</kbd> nav
 <kbd className="rounded bg-surface-sunken px-1.5 py-0.5">esc</kbd> close
 </span>
 </div>
 </Command>
 </div>
 );
}
